/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import {
  ExtendedTooltip,
  FlexContainer,
  IndeterminateCheckbox,
  SVGIcon,
  theme,
} from "@nordcloud/gnui";
import {
  Resource,
  ResourceStatus,
  UpdateStatusForResource,
} from "~/generated/graphql";

import type { ResourceComplianceStatusInfo } from "~/generated/graphql";
import {
  FlexColumnMax,
  LargeIconColumn,
  MediumColumn,
  MediumIconColumn,
  SmallColumn,
  SmallIconColumn,
} from "~/components/Tables/styles";
import { ROUTES } from "~/routing/routes";
import { ComplianceStatusButton } from "~/views/resources/ResourceUpdateStatus";
import {
  complianceInfoTooltip,
  updateStatusInfoTooltip,
} from "~/views/resources/ResourceUpdateStatus/const";
import { ResourceDetailsStatus } from "../ResourceDetails/components/ResourceDetailsStatus";
import { AccountRegionCell } from "./AccountRegionCell";
import { Actions } from "./Actions";
import { NameCell } from "./NameCell";
import { NextPatchingActionCell } from "./NextPatchingActionCell";
import { ResourceGroupCell } from "./ResourceGroupCell";
import { ResourceUpdateStatusCell } from "./ResourceUpdateStatusCell";

const columnHelper = createColumnHelper<Resource>();

export function columns({
  showAccountColumn,
  updateStatuses,
  updateStatusesLoading,
  complianceStatuses,
  complianceStatusesLoading,
}: {
  showAccountColumn: boolean;
  updateStatuses: UpdateStatusForResource[];
  updateStatusesLoading: boolean;
  complianceStatuses: ResourceComplianceStatusInfo[];
  complianceStatusesLoading: boolean;
}) {
  return [
    columnHelper.accessor(() => null, {
      id: "select",
      header: ({ table }) => (
        <IndeterminateCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }) => (
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      ),
      meta: SmallIconColumn,
    }),
    columnHelper.accessor("state", {
      header: () => null,
      cell: ({ row }) => {
        return (
          <FlexContainer>
            <ResourceDetailsStatus
              os={row.original?.os}
              status={
                row.original?.state
                  ? row.original.state.status
                  : ResourceStatus.ConnectionLost
              }
              iconSize="md"
              boxes={false}
            />
          </FlexContainer>
        );
      },
      meta: LargeIconColumn,
    }),
    columnHelper.accessor("name", {
      header: () => "Resource Name / Type",
      cell: ({ row }) => {
        return (
          <NameCell
            to={ROUTES.resources.generateDetails(row.original?.id ?? "")}
            provider={row.original?.provider}
          >
            {row.original?.name}
          </NameCell>
        );
      },
      meta: FlexColumnMax,
    }),
    columnHelper.accessor(() => null, {
      id: "accountRegion",
      header: () => (showAccountColumn ? "Account / Region" : "Region"),
      cell: ({ row }) => {
        const linkTo = row.original.cloudAccount?.id
          ? ROUTES.accounts.generateDetails(row.original.cloudAccount.id)
          : undefined;
        const nameOrId =
          row.original.cloudAccount?.name ?? row.original.providerId;
        return (
          <AccountRegionCell
            linkTo={linkTo}
            region={row.original.region}
            showAccount={showAccountColumn}
          >
            {nameOrId}
          </AccountRegionCell>
        );
      },
      meta: showAccountColumn ? MediumColumn : SmallColumn,
    }),
    columnHelper.accessor("groupIdentifiers", {
      id: "resourceGroups",
      header: () => "Resource Group",
      cell: ({ row }) => (
        <ResourceGroupCell groups={row.original.groupIdentifiers} />
      ),
      meta: MediumColumn,
    }),
    columnHelper.accessor("futurePatchingActions", {
      id: "futurePatchingActions",
      header: () => "Next Patching Action (local time)",
      cell: ({ row }) => (
        <NextPatchingActionCell actions={row.original.futurePatchingActions} />
      ),
      meta: MediumColumn,
    }),
    columnHelper.accessor("id", {
      id: "updateStatus",
      header: () => (
        <FlexContainer alignItems="center" gap={theme.spacing.spacing02}>
          <span>Last Update Status</span>
          <ExtendedTooltip caption={updateStatusInfoTooltip}>
            <SVGIcon name="info" size="smd" css={{ display: "block" }} />
          </ExtendedTooltip>
        </FlexContainer>
      ),
      cell: ({ row }) => {
        return (
          <ResourceUpdateStatusCell
            updateStatusesLoading={updateStatusesLoading}
            updateStatuses={updateStatuses}
            resource={row.original}
          />
        );
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor("id", {
      id: "compliance",
      header: () => (
        <FlexContainer alignItems="center" gap={theme.spacing.spacing02}>
          <span>Compliance</span>
          <ExtendedTooltip caption={complianceInfoTooltip}>
            <SVGIcon name="info" size="smd" css={{ display: "block" }} />
          </ExtendedTooltip>
        </FlexContainer>
      ),
      cell: ({ row }) => {
        return (
          <ComplianceStatusButton
            complianceStatusesLoading={complianceStatusesLoading}
            complianceStatuses={complianceStatuses}
            resource={row.original}
          />
        );
      },
      meta: SmallColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "options",
      header: () => null,
      cell: ({ row }) => {
        const resourcesDetails = [
          {
            id: row.original?.id ?? "",
            name: row.original?.name ?? "",
            scanned: row.original?.scanned,
            os: row.original?.os?.type ?? undefined,
          },
        ];
        return <Actions {...{ resourcesDetails }} />;
      },
      meta: MediumIconColumn,
    }),
  ] as never;
}
